import React, { useState, useEffect } from 'react';
import { X, Database, Clock, Settings, Shield } from 'lucide-react';
import { useCache } from '../../context/CacheContext';

interface CacheConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CacheConsentModal({ isOpen, onClose }: CacheConsentModalProps) {
  const { cacheSettings, updateCacheSettings } = useCache();
  const [showCustomize, setShowCustomize] = useState(false);
  const [localSettings, setLocalSettings] = useState(cacheSettings);
  const [countdown, setCountdown] = useState(6);

  useEffect(() => {
    if (isOpen) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleAcceptRequired();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-accept required settings after 6 seconds
      const acceptTimer = setTimeout(() => {
        handleAcceptRequired();
      }, 6000);

      return () => {
        clearInterval(timer);
        clearTimeout(acceptTimer);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAcceptAll = () => {
    updateCacheSettings({
      enabled: true,
      duration: 24,
      types: {
        funds: true,
        stocks: true,
        analytics: true,
        preferences: true
      }
    });
    localStorage.setItem('cache_consent_given', 'true');
    localStorage.setItem('cache_consent_type', 'all');
    onClose();
  };

  const handleAcceptRequired = () => {
    updateCacheSettings({
      enabled: true,
      duration: 24,
      types: {
        funds: true,
        stocks: true,
        analytics: false,
        preferences: false
      }
    });
    localStorage.setItem('cache_consent_given', 'true');
    localStorage.setItem('cache_consent_type', 'required');
    onClose();
  };

  const handleSaveCustom = () => {
    updateCacheSettings(localSettings);
    localStorage.setItem('cache_consent_given', 'true');
    localStorage.setItem('cache_consent_type', 'custom');
    onClose();
  };

  const handleReject = () => {
    updateCacheSettings({
      enabled: false,
      duration: 24,
      types: {
        funds: false,
        stocks: false,
        analytics: false,
        preferences: false
      }
    });
    localStorage.setItem('cache_consent_given', 'true');
    localStorage.setItem('cache_consent_type', 'none');
    onClose();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 transform transition-transform duration-300 ease-in-out">
      <div className="bg-[#143D60] text-[#DDEB9D] shadow-lg border-t border-[#A0C878]">
        {!showCustomize ? (
          <div className="max-w-7xl mx-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <Shield className="h-6 w-6 text-[#DDEB9D] mr-2" />
                <h3 className="text-lg font-semibold">Cookie & Cache Preferences</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-[#A0C878]/20"
              >
                <X className="h-5 w-5 text-[#DDEB9D]" />
              </button>
            </div>
            
            <p className="nature-text mb-6">
              We use cookies and local cache to improve your experience, provide personalized content, 
              and analyze our traffic. Select your preferences below.
            </p>

            <div className="flex justify-end space-x-4">
              <button
                onClick={handleReject}
                className="px-4 py-2 text-sm font-medium text-[#DDEB9D] hover:bg-[#A0C878]/20 rounded-md"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="px-4 py-2 text-sm font-medium text-[#DDEB9D] hover:bg-[#A0C878]/20 rounded-md"
              >
                Customize
              </button>
              <button
                onClick={handleAcceptRequired}
                className="px-4 py-2 text-sm font-medium bg-[#A0C878] text-[#143D60] hover:bg-[#A0C878]/90 rounded-md"
              >
                Accept Required ({countdown}s)
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium bg-[#DDEB9D] text-[#143D60] hover:bg-[#DDEB9D]/90 rounded-md"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto p-6">
            <div className="flex items-start justify-between mb-6">
              <h3 className="text-lg font-semibold">Customize Cookie Settings</h3>
              <button
                onClick={() => setShowCustomize(false)}
                className="p-1 rounded-full hover:bg-[#A0C878]/20"
              >
                <X className="h-5 w-5 text-[#DDEB9D]" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-[#143D60]/50 rounded-lg border border-[#A0C878]/30">
                <div>
                  <h4 className="font-medium">Required Cookies</h4>
                  <p className="text-sm text-[#DDEB9D]/80">Essential for website functionality</p>
                </div>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="h-4 w-4 text-[#A0C878] rounded border-[#A0C878]"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-[#143D60]/50 rounded-lg border border-[#A0C878]/30">
                <div>
                  <h4 className="font-medium">Analytics</h4>
                  <p className="text-sm text-[#DDEB9D]/80">Help us improve our website</p>
                </div>
                <input
                  type="checkbox"
                  checked={localSettings.types.analytics}
                  onChange={(e) => setLocalSettings(prev => ({
                    ...prev,
                    types: { ...prev.types, analytics: e.target.checked }
                  }))}
                  className="h-4 w-4 text-[#A0C878] rounded border-[#A0C878]"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-[#143D60]/50 rounded-lg border border-[#A0C878]/30">
                <div>
                  <h4 className="font-medium">Preferences</h4>
                  <p className="text-sm text-[#DDEB9D]/80">Store your preferences and settings</p>
                </div>
                <input
                  type="checkbox"
                  checked={localSettings.types.preferences}
                  onChange={(e) => setLocalSettings(prev => ({
                    ...prev,
                    types: { ...prev.types, preferences: e.target.checked }
                  }))}
                  className="h-4 w-4 text-[#A0C878] rounded border-[#A0C878]"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-[#143D60]/50 rounded-lg border border-[#A0C878]/30">
                <div>
                  <h4 className="font-medium">Cache Duration</h4>
                  <p className="text-sm text-[#DDEB9D]/80">How long to store cached data</p>
                </div>
                <select
                  value={localSettings.duration}
                  onChange={(e) => setLocalSettings(prev => ({
                    ...prev,
                    duration: parseInt(e.target.value)
                  }))}
                  className="rounded-md bg-[#143D60] border-[#A0C878] text-[#DDEB9D] focus:border-[#DDEB9D] focus:ring focus:ring-[#DDEB9D]/20"
                >
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={48}>48 hours</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setShowCustomize(false)}
                className="px-4 py-2 text-sm font-medium text-[#DDEB9D] hover:bg-[#A0C878]/20 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCustom}
                className="px-4 py-2 text-sm font-medium bg-[#DDEB9D] text-[#143D60] hover:bg-[#DDEB9D]/90 rounded-md"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}